import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, createCommentVNode as _createCommentVNode, vShow as _vShow, withCtx as _withCtx, withDirectives as _withDirectives, createElementVNode as _createElementVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-7bcfecc4"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "selected-image-options"
}
const _hoisted_2 = { class: "button-container" }
const _hoisted_3 = { class: "file-uploader" }

import { displayQuestionAlert } from "@/alerts/questionAlert";
import CarouselPicture from "@/components/carousel-pictures/carousel-picture.vue";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import { PictureIds } from "@/types/picture-management/picture-ids";
import displayErrorMessage from "@/utils/ErrorHandling";
import { PropertyImageType } from "acquaint-api/constants/PropertyImageType";
import {
  computed,
  inject,
  ref,
  nextTick,
  watch,
  PropType,
  onMounted,
} from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import FileUpload from "@/components/ui/inputs/file-upload.vue";
import DxCheckBox from "devextreme-vue/check-box";
import { Properties } from "acquaint-api/models/Properties";
import { getErrorMessage } from "bright-logic-core/src/errors";
import { standardInfoAlert } from "@/alerts/standardInfoAlert";
import { CarouselPictureType } from "@/enums/CarouselRecordType";
import { DxButton } from "devextreme-vue";
import ScrollView from "@/components/ui/scroll-view.vue";
import ImagerViewerModal from "@/components/carousel-pictures/image-viewer-modal.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'property-listing-picture-management',
  props: {
  refreshData: {
    type: Boolean,
    required: true,
  },
  /**
   * The unique identifier for the record the pictures belong to.
   * @type {string}
   * @required
   */
  recordId: {
    type: Number,
    required: true,
  },
  /**
   * The count of the image to be displayed.
   * @type {number}
   * @required
   */
  pictureCount: {
    type: Number,
    required: true,
  },
  /**
   * Determines the type of record the pictures belong to.
   */
  recordType: {
    type: Number as PropType<CarouselPictureType>,
    default: CarouselPictureType.Property,
  },
},
  emits: [
  /**
   * Emitted when the image list is updated, either by adding or deleting.
   */
  "image-list-updated",
],
  setup(__props, { emit }) {

const props = __props;



const numberPictures = ref();
const imageList = ref<PictureIds[]>([]);
const disableButtons = ref(false);
const propertyAllData = ref<Properties>();
const resetFiles = ref(false);
const safeMode = ref(false);
const pictureLoadingErrorDisplayed = ref(false);
const apiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const triggerFileUploading = ref(false);
const isWebMainPicture = (imageId: number) =>
  imageId === propertyAllData.value?.pictureWebMain;
const rearrangeDisabled = computed(
  () => safeMode.value || disableButtons.value,
);
const photoContainerClasses = computed(() =>
  disableButtons.value
    ? ["photo-container", "processing"]
    : ["photo-container"],
);
const autoScrollToBottom = ref(false);
const selectedItem = ref(-1);
const itemSelected = ref(false);
const uploadFailed = ref(false);
const selectedImageId = ref(-1);
const displayZoomModal = ref(false);

watch(
  () => props.refreshData,
  async () => {
    itemSelected.value = false;
    if (!props.refreshData) {
      pictureLoadingErrorDisplayed.value = false;
      safeMode.value = false;
      return;
    }

    if (props.recordType == CarouselPictureType.Property) {
      await fetchAllPropertyData();
    }

    // When modal is shown, populate the image list so the VueDraggable component can track the images
    populateImageList();
  },
);

onMounted(async () => {
  itemSelected.value = false;
  if (props.recordType == CarouselPictureType.Property) {
    await fetchAllPropertyData();
  }

  // When modal is shown, populate the image list so the VueDraggable component can track the images
  populateImageList();
});

const fetchAllPropertyData = async () => {
  try {
    propertyAllData.value = await apiClient.httpGet(
      `database/properties/${props.recordId}`,
    );
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
  }
};

/** Pre-populates the image list array for the VueDraggable component to bind to */
const populateImageList = () => {
  if (imageList.value.length > 0) {
    return;
  }

  numberPictures.value = props.pictureCount;
  imageList.value = [];
  pictureLoadingErrorDisplayed.value = false;
  safeMode.value = false;

  if (
    !propertyAllData.value &&
    props.recordType == CarouselPictureType.Property
  ) {
    return;
  }

  for (let i = 0; i < numberPictures.value; i++) {
    imageList.value.push({ id: i + 1, key: generateImageKey(i + 1) });
  }
};

/** Generates a semi-random image key. This is used to give the VueDraggable component a unique key for each image
 *  This avoids incorrect images being removed from the screen when deleting images
 * @param id id of the image to generate a key for */
const generateImageKey = (id: number) =>
  id.toString() + Math.random().toString().replace("0.", "");

const resetProcessingFields = () => {
  itemSelected.value = false;
  selectedItem.value = -1;
  disableButtons.value = false;
};

const onErrorLoadingPicture = () => {
  safeMode.value = true;

  // To avoid annoying the user, only display the safe mode prompt once
  if (!pictureLoadingErrorDisplayed.value) {
    standardInfoAlert(
      "One or more pictures cannot be found. The Manage Pictures screen has opened in Safe Mode, certain features will be restricted",
    );
    pictureLoadingErrorDisplayed.value = true;
  }
};

const onImageLoaded = (imageId: number) => {
  /* To avoid constant auto scrolling, which could annoy users if they manually scroll after adding a picture
   * After the last image has been added, reset the auto scroll indicator.
   * This needs to be done on a timeout, to allow the browser time to display the image */
  if (autoScrollToBottom.value === true && imageId === imageList.value.length) {
    setTimeout(() => (autoScrollToBottom.value = false), 600);
  }
};

// Logic for moving and selecting images
const movePicturePlace = async (e: any) => {
  disableButtons.value = true;

  let success = false;

  const url =
    props.recordType == CarouselPictureType.Property
      ? `app/properties/${props.recordId}/pictures/move`
      : `app/workorders/${props.recordId}/pictures/rearrange`;

  try {
    await apiClient.httpPostVoid(url, {
      fromPictureNumber: e.moved.oldIndex + 1,
      toPictureNumber: e.moved.newIndex + 1,
    });
    success = true;
  } catch (err) {
    success = false;
    displayErrorMessage("We could not move the position of this picture.");
  }

  resetProcessingFields();
  return success;
};

const selectImage = (index: number, imageId: number) => {
  if (disableButtons.value) {
    return;
  }

  if (selectedItem.value == index) {
    itemSelected.value = false;
    selectedItem.value = -1;
    selectedImageId.value = -1;
  } else {
    itemSelected.value = true;
    selectedItem.value = index;
    selectedImageId.value = imageId;
  }
};

// Logic for deleting images
const onDeleteClick = async () => {
  await deletePhoto(selectedImageId.value);
};

const deletePhoto = async (pictureNumber: number) => {
  let success = true;

  const result = await displayQuestionAlert(
    "Are you sure you want to delete this picture?",
  );

  if (result.isDenied) {
    return false;
  }

  // If a user tries to delete an image, they shouldn't be able to interact with the buttons while the api call is being amde
  disableButtons.value = true;

  const deleteURL =
    props.recordType == CarouselPictureType.Property
      ? `app/properties/${props.recordId}/pictures/${pictureNumber}`
      : `app/workorders/${props.recordId}/pictures/${pictureNumber}`;

  try {
    await apiClient.httpDeleteVoid(deleteURL);
  } catch (err) {
    success = false;
    displayErrorMessage("We could not delete of this picture.");
  }

  // If the api call has failed, the users should be able to interact with the buttons again
  if (!success) {
    disableButtons.value = false;
    return false;
  }

  // Instead of refreshing the whole screen, just remove the picture that was successfully deleted
  imageList.value.splice(pictureNumber - 1, 1);
  numberPictures.value = numberPictures.value - 1;

  // As all pictures will now have a new id, loop through pictures array and reset the id
  for (let i = 0; i < imageList.value.length; i++) {
    imageList.value[i].id = i + 1;
  }

  resetProcessingFields();
  emit("image-list-updated", numberPictures.value);
  return true;
};

const onDisplayZoomModal = () => {
  displayZoomModal.value = true;
};

// This opens the file explorer so users can select and upload images
const onAddClick = () => {
  disableButtons.value = true;
  triggerFileUploading.value = true;
};

const uploadAddedImage = async (e: File[]) => {
  if (!e) {
    resetProcessingFields();
    return;
  }

  uploadFailed.value = false;
  triggerFileUploading.value = false;
  resetFiles.value = true;

  for (let i = 0; i < e.length; i++) {
    if (!(await submitImageToAPI(e[i]))) {
      resetProcessingFields();
      resetFiles.value = false;
      continue;
    }

    numberPictures.value++;

    await nextTick();

    imageList.value.push({
      id: numberPictures.value,
      key: generateImageKey(numberPictures.value),
    });
  }
  resetFiles.value = false;
  resetProcessingFields();
  autoScrollToBottom.value = true;
  emit("image-list-updated", numberPictures.value);
};

const submitImageToAPI = async (image: File) => {
  const url =
    props.recordType == CarouselPictureType.Property
      ? `app/properties/${props.recordId}/pictures`
      : `app/workorders/${props.recordId}/pictures`;

  try {
    /* As the image data is a byte array and not json, we need to use uploadFile instead of httpPost
     * Otherwise the api will error*/
    await apiClient.uploadFile(url, image);
    return true;
  } catch (err) {
    displayErrorMessage(
      "We could not upload your image, please ensure it is in a .jpg, .jpeg or .gif formats.",
    );
    uploadFailed.value = true;
    return false;
  }
};

const onUploadCancelled = () => {
  triggerFileUploading.value = false;
  resetProcessingFields();
};

// For making the selected image the primary web picture
const makeMainImage = async (e: any, pictureNumber: number) => {
  if (!propertyAllData.value) {
    return;
  }

  // If the checkbox has been set to false and/or then back to true for main picture, there is no point calling the API.
  // In desktop acquaint, if a user deselects the main web image without selecting a new image to be the main web image,
  // nothing occurs - the main web image remains the same.
  if (propertyAllData.value.pictureWebMain == pictureNumber) {
    return;
  }
  propertyAllData.value.pictureWebMain = pictureNumber;

  try {
    await apiClient.httpPutVoid(`database/properties/${props.recordId}`, {
      pictureWebMain: pictureNumber,
    });
  } catch (err) {
    displayErrorMessage("We could not save any changes made.");
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("section", {
      class: _normalizeClass(photoContainerClasses.value)
    }, [
      _createVNode(ScrollView, null, {
        default: _withCtx(() => [
          _withDirectives(_createVNode(_unref(VueDraggableNext), {
            class: "dragArea list-group draggable-section",
            modelValue: imageList.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((imageList).value = $event)),
            onChange: movePicturePlace,
            "item-key": "key",
            disabled: rearrangeDisabled.value
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(imageList.value, (image, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  class: "picture-holder",
                  key: image.key
                }, [
                  _createVNode(CarouselPicture, {
                    class: _normalizeClass({ selectedItem: selectedItem.value == index && itemSelected.value }),
                    "record-id": props.recordId,
                    pictureNumber: image.id,
                    "record-type": props.recordType,
                    type: _unref(PropertyImageType).PropertyImageWeb,
                    onClick: ($event: any) => (selectImage(index, image.id)),
                    onErrorLoadingPicture: onErrorLoadingPicture,
                    onLoaded: ($event: any) => (onImageLoaded(image.id))
                  }, null, 8, ["class", "record-id", "pictureNumber", "record-type", "type", "onClick", "onLoaded"]),
                  (
              selectedItem.value == index &&
              itemSelected.value &&
              props.recordType == _unref(CarouselPictureType).Property
            )
                    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                        _createVNode(_unref(DxCheckBox), {
                          text: "Web Main Picture",
                          value: isWebMainPicture(image.id),
                          onValueChanged: (e) => makeMainImage(e, image.id),
                          readOnly: disableButtons.value
                        }, null, 8, ["value", "onValueChanged", "readOnly"])
                      ]))
                    : _createCommentVNode("", true)
                ]))
              }), 128))
            ]),
            _: 1
          }, 8, ["modelValue", "disabled"]), [
            [_vShow, numberPictures.value > 0]
          ])
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_2, [
        _createVNode(_unref(DxButton), {
          text: "Delete Photo",
          onClick: _cache[1] || (_cache[1] = () => onDeleteClick()),
          disabled: selectedItem.value == -1
        }, null, 8, ["disabled"]),
        _createVNode(_unref(DxButton), {
          text: "Add Photo",
          onClick: _cache[2] || (_cache[2] = () => onAddClick())
        }),
        _createVNode(_unref(DxButton), {
          text: "Zoom",
          onClick: _cache[3] || (_cache[3] = () => onDisplayZoomModal()),
          disabled: selectedItem.value == -1
        }, null, 8, ["disabled"])
      ]),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(FileUpload, {
          text: "Add Photo",
          onFilesChanged: uploadAddedImage,
          showFileList: false,
          readOnly: false,
          buttonBackground: "#0070d2",
          buttonTextColor: "white",
          triggerFileUploading: triggerFileUploading.value,
          reset: resetFiles.value,
          multiple: true,
          onUploadCancelled: onUploadCancelled
        }, null, 8, ["triggerFileUploading", "reset"])
      ])
    ], 2),
    _createVNode(ImagerViewerModal, {
      modelValue: displayZoomModal.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((displayZoomModal).value = $event)),
      imageList: imageList.value,
      "record-id": props.recordId.toString(),
      intialImageToDisplay: selectedImageId.value,
      recordType: props.recordType
    }, null, 8, ["modelValue", "imageList", "record-id", "intialImageToDisplay", "recordType"])
  ], 64))
}
}

})